import { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { UserPlus, X, Send, Users, Mail, Phone, User, Sparkles, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createReferral, checkReferralReferrer, checkReferralContact } from '@/lib/api';

interface Contact {
  name: string;
  email: string;
  phone: string;
}

const emptyContact = (): Contact => ({ name: '', email: '', phone: '' });

const INVITE_GOALS = [5, 10, 15] as const;
type InviteGoal = (typeof INVITE_GOALS)[number];

const PROGRESS_COLORS = [
  'bg-africa-red',
  'bg-africa-orange',
  'bg-africa-yellow',
  'bg-africa-green',
  'bg-africa-blue',
  'bg-africa-purple',
  'bg-africa-red',
  'bg-africa-green',
  'bg-africa-blue',
  'bg-africa-orange',
];

function fireConfetti() {
  const count = 120;
  const defaults = { origin: { y: 0.7 }, zIndex: 9999 };
  confetti({ ...defaults, particleCount: count, spread: 80 });
  confetti({ ...defaults, particleCount: count * 0.6, angle: 60, spread: 55 });
  confetti({ ...defaults, particleCount: count * 0.6, angle: 120, spread: 55 });
}

const DESCRIBES_YOU_OPTIONS = [
  'Student',
  'Business owner/Entrepreneur',
  'Content Creator',
  'Freelancer',
  'Job Seeker',
  'Other',
] as const;
const AI_KNOWLEDGE_OPTIONS = ['Beginner', 'Intermediate', 'Advanced'] as const;
const INTERESTS_OPTIONS = [
  'AI tools (ChatGPT, Midjourney, etc.)',
  'Building AI products',
  'Making money with AI',
  'Automation',
  'Networking & opportunities',
] as const;
const WOULD_LIKE_OPTIONS = ['Just learn', 'Actively build projects', 'Find collaborators'] as const;

const STORAGE_KEY = 'homeland-referral-draft';
const LAST_SUBMISSION_KEY = 'homeland-referral-last-submission';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

// Common domain typos so invite emails get delivered (fix on blur / show hint).
const DOMAIN_TYPO_FIXES: Record<string, string> = {
  'gmal.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'googlemail.com': 'gmail.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'icloud.con': 'icloud.com',
};

function fixEmailDomainTypo(email: string): string {
  const e = normalizeEmail(email);
  if (!e || !e.includes('@')) return e;
  const [local, domain] = e.split('@');
  if (!local || !domain) return e;
  const fixed = DOMAIN_TYPO_FIXES[domain];
  return fixed ? `${local}@${fixed}` : e;
}

function isValidEmail(email: string): boolean {
  const e = normalizeEmail(email);
  if (!e) return false;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)) return false;
  const [local, domain] = e.split('@');
  if (!local?.length || !domain?.length) return false;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
  return true;
}

function getEmailError(email: string): string | null {
  const e = email.trim();
  if (!e) return 'Email is required';
  const normalized = normalizeEmail(email);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized)) return 'Enter a valid email (e.g. you@gmail.com, you@yahoo.com)';
  const [, domain] = normalized.split('@');
  const typoFix = domain ? DOMAIN_TYPO_FIXES[domain] : null;
  if (typoFix) return `Did you mean …@${typoFix}?`;
  return null;
}

function getPhoneError(phone: string): string | null {
  const p = phone.trim();
  if (!p) return 'Phone is required';
  if (!isValidKenyanPhone(phone)) return 'Enter a valid Kenyan number (e.g. 0712345678 or +254...)';
  return null;
}

function normalizeKenyanPhone(input: string): string {
  const raw = input.trim();
  if (!raw) return '';
  const plus = raw.startsWith('+');
  const digits = raw.replace(/[^\d]/g, '');

  // Accept +2547XXXXXXXX / +2541XXXXXXXX
  if (plus && digits.startsWith('254') && digits.length === 12) {
    return `+${digits}`;
  }

  // Accept 2547XXXXXXXX / 2541XXXXXXXX
  if (digits.startsWith('254') && digits.length === 12) {
    return `+${digits}`;
  }

  // Accept 07XXXXXXXX / 01XXXXXXXX
  if (digits.startsWith('0') && digits.length === 10) {
    return `+254${digits.slice(1)}`;
  }

  // Accept 7XXXXXXXX / 1XXXXXXXX
  if ((digits.startsWith('7') || digits.startsWith('1')) && digits.length === 9) {
    return `+254${digits}`;
  }

  return raw;
}

function isValidKenyanPhone(phone: string): boolean {
  const p = normalizeKenyanPhone(phone);
  return /^\+254(7|1)\d{8}$/.test(p);
}

function buildSubmissionFingerprint(referrerEmail: string, contacts: Contact[]): string {
  const normalizedEmail = normalizeEmail(referrerEmail);
  const normalizedContacts = contacts
    .map((c) => ({
      name: c.name.trim().toLowerCase(),
      email: normalizeEmail(c.email),
      phone: normalizeKenyanPhone(c.phone),
    }))
    .sort((a, b) => {
      const aKey = a.email || a.phone || a.name;
      const bKey = b.email || b.phone || b.name;
      return aKey.localeCompare(bKey);
    });
  return JSON.stringify({ referrerEmail: normalizedEmail, contacts: normalizedContacts });
}

interface PersistedState {
  step: 1 | 2;
  referrerName: string;
  referrerEmail: string;
  referrerPhone: string;
  savedContacts: Contact[];
  draftContact: Contact;
  showAddForm: boolean;
  inviteGoal: InviteGoal;
  describesYou: string;
  describesYouOther: string;
  aiKnowledge: string;
  interests: string[];
  wantToGain: string;
  wouldLikeTo: string;
}

function loadPersisted(): PersistedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PersistedState;
    if (!data || typeof data.step !== 'number') return null;
    return {
      step: data.step === 2 ? 2 : 1,
      referrerName: typeof data.referrerName === 'string' ? data.referrerName : '',
      referrerEmail: typeof data.referrerEmail === 'string' ? data.referrerEmail : '',
      referrerPhone: typeof data.referrerPhone === 'string' ? data.referrerPhone : '',
      savedContacts: Array.isArray(data.savedContacts)
        ? data.savedContacts.filter(
            (c: unknown) =>
              c && typeof c === 'object' && 'name' in c && typeof (c as Contact).name === 'string'
          ).map((c: { name: string; email?: string; phone?: string }) => ({
            name: String(c.name ?? ''),
            email: typeof c.email === 'string' ? c.email : '',
            phone: typeof c.phone === 'string' ? c.phone : '',
          }))
        : [],
      draftContact:
        data.draftContact && typeof data.draftContact === 'object'
          ? {
              name: String((data.draftContact as Contact).name ?? ''),
              email: String((data.draftContact as Contact).email ?? ''),
              phone: String((data.draftContact as Contact).phone ?? ''),
            }
          : emptyContact(),
      showAddForm: typeof data.showAddForm === 'boolean' ? data.showAddForm : true,
      inviteGoal: [5, 10, 15].includes(Number(data.inviteGoal)) ? (Number(data.inviteGoal) as InviteGoal) : 5,
      describesYou: typeof data.describesYou === 'string' ? data.describesYou : '',
      describesYouOther: typeof data.describesYouOther === 'string' ? data.describesYouOther : '',
      aiKnowledge: typeof data.aiKnowledge === 'string' ? data.aiKnowledge : '',
      interests: Array.isArray(data.interests) ? data.interests.filter((i): i is string => typeof i === 'string') : [],
      wantToGain: typeof data.wantToGain === 'string' ? data.wantToGain : '',
      wouldLikeTo: typeof data.wouldLikeTo === 'string' ? data.wouldLikeTo : '',
    };
  } catch {
    return null;
  }
}

function savePersisted(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

export function ReferralForm() {
  const persistedOnce = useRef<PersistedState | null>(null);
  const getPersisted = () => {
    if (persistedOnce.current === null) persistedOnce.current = loadPersisted();
    return persistedOnce.current;
  };

  const [step, setStep] = useState<1 | 2>(() => getPersisted()?.step ?? 1);
  const [referrerName, setReferrerName] = useState(() => getPersisted()?.referrerName ?? '');
  const [referrerEmail, setReferrerEmail] = useState(() => getPersisted()?.referrerEmail ?? '');
  const [referrerPhone, setReferrerPhone] = useState(() => getPersisted()?.referrerPhone ?? '');
  const [savedContacts, setSavedContacts] = useState<Contact[]>(() => getPersisted()?.savedContacts ?? []);
  const [draftContact, setDraftContact] = useState<Contact>(() => getPersisted()?.draftContact ?? emptyContact());
  const [showAddForm, setShowAddForm] = useState(() => getPersisted()?.showAddForm ?? true);
  const [inviteGoal, setInviteGoal] = useState<InviteGoal>(() => getPersisted()?.inviteGoal ?? 5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [thankYouInviteCount, setThankYouInviteCount] = useState(0);
  const confettiMilestones = useRef<Set<number>>(new Set());
  const [describesYou, setDescribesYou] = useState<string>(() => getPersisted()?.describesYou ?? '');
  const [describesYouOther, setDescribesYouOther] = useState<string>(() => getPersisted()?.describesYouOther ?? '');
  const [aiKnowledge, setAiKnowledge] = useState<string>(() => getPersisted()?.aiKnowledge ?? '');
  const [interests, setInterests] = useState<string[]>(() => getPersisted()?.interests ?? []);
  const [wantToGain, setWantToGain] = useState(() => getPersisted()?.wantToGain ?? '');
  const [wouldLikeTo, setWouldLikeTo] = useState<string>(() => getPersisted()?.wouldLikeTo ?? '');
  const [referrerEmailError, setReferrerEmailError] = useState<string | null>(null);
  const [referrerPhoneError, setReferrerPhoneError] = useState<string | null>(null);
  const [draftEmailError, setDraftEmailError] = useState<string | null>(null);
  const [draftPhoneError, setDraftPhoneError] = useState<string | null>(null);

  useEffect(() => {
    savePersisted({
      step,
      referrerName,
      referrerEmail,
      referrerPhone,
      savedContacts,
      draftContact,
      showAddForm,
      inviteGoal,
      describesYou,
      describesYouOther,
      aiKnowledge,
      interests,
      wantToGain,
      wouldLikeTo,
    });
  }, [step, referrerName, referrerEmail, referrerPhone, savedContacts, draftContact, showAddForm, inviteGoal, describesYou, describesYouOther, aiKnowledge, interests, wantToGain, wouldLikeTo]);

  useEffect(() => {
    const n = savedContacts.length;
    if (INVITE_GOALS.includes(n as InviteGoal) && !confettiMilestones.current.has(n)) {
      confettiMilestones.current.add(n);
      fireConfetti();
    }
  }, [savedContacts.length]);

  const toggleInterest = (value: string) => {
    setInterests((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]
    );
  };

  const addContact = async () => {
    const name = draftContact.name.trim();
    const email = draftContact.email.trim();
    const phone = draftContact.phone.trim();
    if (!name) {
      toast.error('Please enter the person\'s name');
      return;
    }
    if (!email || !phone) {
      toast.error('Email and phone are required');
      setDraftContact(emptyContact());
      return;
    }
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      setDraftContact(emptyContact());
      return;
    }
    if (!isValidKenyanPhone(phone)) {
      toast.error('Please enter a valid Kenyan phone number (e.g. 0712345678)');
      setDraftContact(emptyContact());
      return;
    }
    const formattedPhone = normalizeKenyanPhone(phone);
    const formattedEmail = normalizeEmail(email);
    const duplicate = savedContacts.some((c) => {
      const existingEmail = c.email.trim().toLowerCase();
      const existingPhone = c.phone.trim();
      return (
        (formattedEmail && existingEmail === formattedEmail) ||
        (formattedPhone && existingPhone === formattedPhone)
      );
    });
    if (duplicate) {
      toast.error('You already added this person.');
      setDraftContact(emptyContact());
      return;
    }
    if (savedContacts.length >= 15) {
      toast.error('Maximum 15 contacts allowed');
      return;
    }
    // Check against existing invitations in the database
    try {
      const dbCheck = await checkReferralContact({ email: formattedEmail, phone: formattedPhone });
      if (dbCheck.exists) {
        toast.error('This person has already been invited before.');
        setDraftContact(emptyContact());
        return;
      }
    } catch {
      toast.error('Could not verify this contact. Please try again.');
      setDraftContact(emptyContact());
      return;
    }
    setSavedContacts((prev) => [...prev, { name, email: formattedEmail, phone: formattedPhone }]);
    setDraftContact(emptyContact());
    setShowAddForm(false);
    toast.success('Person added');
  };

  const removeSavedContact = (index: number) => {
    const prevLength = savedContacts.length;
    setSavedContacts((prev) => prev.filter((_, i) => i !== index));
    if (INVITE_GOALS.includes(prevLength as InviteGoal)) {
      confettiMilestones.current.delete(prevLength);
    }
  };

  const updateDraft = (field: keyof Contact, value: string) => {
    setDraftContact((prev) => ({ ...prev, [field]: value }));
    if (field === 'email') setDraftEmailError(null);
    if (field === 'phone') setDraftPhoneError(null);
  };

  const openAddForm = () => {
    openAddForm();
    setDraftEmailError(null);
    setDraftPhoneError(null);
  };

  const resetForm = () => {
    setReferrerName('');
    setReferrerEmail('');
    setReferrerPhone('');
    setSavedContacts([]);
    setDraftContact(emptyContact());
    openAddForm();
    setInviteGoal(5);
    confettiMilestones.current = new Set();
    setStep(1);
    setDescribesYou('');
    setDescribesYouOther('');
    setAiKnowledge('');
    setInterests([]);
    setWantToGain('');
    setWouldLikeTo('');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const name = referrerName.trim();
    const email = referrerEmail.trim();
    const phone = referrerPhone.trim();

    if (!name || !email || !phone) {
      toast.error('Please fill in your name, email, and phone');
      return;
    }
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      setReferrerEmail('');
      return;
    }
    if (!isValidKenyanPhone(phone)) {
      toast.error('Please enter a valid Kenyan phone number (e.g. 0712345678)');
      setReferrerPhone('');
      return;
    }
    const formattedReferrerEmail = normalizeEmail(email);
    const formattedReferrerPhone = normalizeKenyanPhone(phone);

    if (describesYou === 'Other' && !describesYouOther.trim()) {
      toast.error('Please specify what best describes you.');
      setDescribesYouOther('');
      return;
    }

    if (savedContacts.length < 5) {
      toast.error(`Please add at least 5 people to invite (you have ${savedContacts.length})`);
      return;
    }

    // Prevent submitting the exact same details repeatedly
    let fingerprint: string | null = null;
    try {
      fingerprint = buildSubmissionFingerprint(referrerEmail, savedContacts);
      const last = localStorage.getItem(LAST_SUBMISSION_KEY);
      if (last && fingerprint === last) {
        toast.error('It looks like you already submitted these same details.');
        resetForm();
        return;
      }
    } catch {
      // ignore fingerprint storage errors
    }

    setIsSubmitting(true);
    try {
      await createReferral({
        referrerName: name,
        referrerEmail: formattedReferrerEmail,
        referrerPhone: formattedReferrerPhone,
        describesYou: describesYou || undefined,
        describesYouOther: describesYou === 'Other' ? describesYouOther.trim() || undefined : undefined,
        aiKnowledge: aiKnowledge || undefined,
        interests: interests.length > 0 ? interests : undefined,
        wantToGain: wantToGain.trim() || undefined,
        wouldLikeTo: wouldLikeTo || undefined,
        contacts: savedContacts.map((c) => ({
          name: c.name.trim(),
          email: normalizeEmail(c.email),
          phone: normalizeKenyanPhone(c.phone),
        })),
      });
      setThankYouInviteCount(savedContacts.length);
      resetForm();
      setShowThankYouModal(true);
      toast.success(`${savedContacts.length} invitation(s) sent successfully!`);
      if (fingerprint) {
        try {
          localStorage.setItem(LAST_SUBMISSION_KEY, fingerprint);
        } catch {
          // ignore
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToStep2 = async (e: React.MouseEvent) => {
    e.preventDefault();
    const name = referrerName.trim();
    const email = referrerEmail.trim();
    const phone = referrerPhone.trim();

    if (!name || !email || !phone) {
      toast.error('Please fill in your name, email, and phone');
      return;
    }
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      setReferrerEmail('');
      return;
    }
    if (!isValidKenyanPhone(phone)) {
      toast.error('Please enter a valid Kenyan phone number (e.g. 0712345678)');
      setReferrerPhone('');
      return;
    }
    const formattedEmail = normalizeEmail(email);
    try {
      const res = await checkReferralReferrer(formattedEmail);
      if (res.exists) {
        toast.error('It looks like this email has already submitted a referral.');
        setReferrerName('');
        setReferrerEmail('');
        setReferrerPhone('');
        return;
      }
      setReferrerEmail(formattedEmail);
      setReferrerPhone(normalizeKenyanPhone(phone));
      setStep(2);
    } catch {
      toast.error('Could not verify your email. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-3xl mx-auto">
      {/* Step 1: Your Details (hidden on step 2) */}
      {step === 1 && (
        <div className="bg-card rounded-xl border border-border p-6 md:p-8 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <User className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-lg font-sans">
                Your Details
              </h3>
              <p className="text-sm text-muted-foreground">Tell us who you are</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label
                htmlFor="referrerName"
                className="text-sm font-medium text-foreground"
              >
                Full Name *
              </Label>
              <Input
                id="referrerName"
                placeholder="John Doe"
                value={referrerName}
                onChange={(e) => setReferrerName(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label
                htmlFor="referrerEmail"
                className="text-sm font-medium text-foreground"
              >
                Email *
              </Label>
              <Input
                id="referrerEmail"
                type="email"
                placeholder="e.g. you@gmail.com, you@yahoo.com"
                value={referrerEmail}
                onChange={(e) => {
                  setReferrerEmail(e.target.value);
                  setReferrerEmailError(null);
                }}
                onBlur={() => {
                  const fixed = fixEmailDomainTypo(referrerEmail);
                  setReferrerEmail(fixed);
                  setReferrerEmailError(getEmailError(fixed));
                }}
                className={cn('mt-1.5', referrerEmailError && 'border-destructive')}
                required
              />
              {referrerEmailError && (
                <p className="mt-1 text-xs text-destructive">{referrerEmailError}</p>
              )}
            </div>
            <div>
              <Label
                htmlFor="referrerPhone"
                className="text-sm font-medium text-foreground"
              >
                Phone *
              </Label>
              <Input
                id="referrerPhone"
                type="tel"
                placeholder="e.g. 0712345678 or +254..."
                value={referrerPhone}
                onChange={(e) => {
                  setReferrerPhone(e.target.value);
                  setReferrerPhoneError(null);
                }}
                onBlur={() => {
                  const normalized = normalizeKenyanPhone(referrerPhone);
                  setReferrerPhone(normalized);
                  setReferrerPhoneError(getPhoneError(normalized));
                }}
                className={cn('mt-1.5', referrerPhoneError && 'border-destructive')}
                required
              />
              {referrerPhoneError && (
                <p className="mt-1 text-xs text-destructive">{referrerPhoneError}</p>
              )}
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              onClick={goToStep2}
              disabled={
                !referrerName.trim() ||
                !referrerEmail.trim() ||
                !referrerPhone.trim() ||
                !!referrerEmailError ||
                !!referrerPhoneError ||
                !isValidEmail(referrerEmail) ||
                !isValidKenyanPhone(referrerPhone)
              }
              className="gap-2 bg-accent text-accent-foreground hover:bg-gold-light disabled:opacity-50"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Your AI Journey + Invite People (hidden on step 1) */}
      {step === 2 && (
        <>
          <div className="bg-card rounded-xl border border-border p-6 md:p-8 mb-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-accent-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-lg font-sans">
                  Your AI Journey
                </h3>
                <p className="text-sm text-muted-foreground">
                  Help us tailor your experience
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-foreground">
                  1. What best describes you?
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {DESCRIBES_YOU_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        setDescribesYou(opt);
                        if (opt !== 'Other') setDescribesYouOther('');
                      }}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                        describesYou === opt
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border bg-background hover:border-accent/50 text-foreground'
                      )}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {describesYou === 'Other' && (
                  <div className="mt-3">
                    <Label htmlFor="describesYouOther" className="text-sm font-medium text-foreground">
                      Please specify
                    </Label>
                    <Input
                      id="describesYouOther"
                      placeholder="e.g. Researcher, Engineer, Teacher…"
                      value={describesYouOther}
                      onChange={(e) => setDescribesYouOther(e.target.value)}
                      className="mt-1.5"
                      required
                    />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-foreground">
                  2. How would you rate your AI knowledge?
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AI_KNOWLEDGE_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setAiKnowledge(opt)}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                        aiKnowledge === opt
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border bg-background hover:border-accent/50 text-foreground'
                      )}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-foreground">
                  3. What are you most interested in? (multi-select)
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {INTERESTS_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleInterest(opt)}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                        interests.includes(opt)
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border bg-background hover:border-accent/50 text-foreground'
                      )}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label
                  htmlFor="wantToGain"
                  className="text-sm font-medium text-foreground"
                >
                  4. What do you want to gain from this group? (optional)
                </Label>
                <Textarea
                  id="wantToGain"
                  placeholder="e.g. Learn to build AI tools, find co-founders..."
                  value={wantToGain}
                  onChange={(e) => setWantToGain(e.target.value)}
                  className="mt-1.5 min-h-[80px]"
                  rows={3}
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-foreground">
                  5. Would you like to:
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {WOULD_LIKE_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setWouldLikeTo(opt)}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                        wouldLikeTo === opt
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border bg-background hover:border-accent/50 text-foreground'
                      )}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6 md:p-8 shadow-sm mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                  <Users className="w-5 h-5 text-accent-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg font-sans">
                    Invite People
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Add at least 5 people (goal: 5, 10, or 15)
                  </p>
                </div>
              </div>
              {savedContacts.length < 15 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openAddForm()}
                  className="gap-1.5 border-accent text-accent hover:bg-accent hover:text-accent-foreground shrink-0"
                >
                  <UserPlus className="w-4 h-4" />
                  Add
                </Button>
              )}
            </div>

            {/* Goal: 3, 5, or 10 */}
            <div className="mb-6">
              <Label className="text-sm font-medium text-foreground mb-2 block">
                Invite goal
              </Label>
              <div className="flex flex-wrap gap-2">
                {INVITE_GOALS.map((goal) => (
                  <button
                    key={goal}
                    type="button"
                    onClick={() => setInviteGoal(goal)}
                    className={cn(
                      'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                      inviteGoal === goal
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border bg-background hover:border-accent/50 text-foreground'
                    )}
                  >
                    {goal} {goal === 5 ? '(minimum)' : ''}
                  </button>
                ))}
              </div>
            </div>

            {/* Count + Add another when we have saved contacts and form is hidden */}
            {savedContacts.length > 0 && !showAddForm && (
              <div className="mb-4 p-4 rounded-lg bg-secondary/50 border border-border">
                <p className="text-foreground font-medium">
                  {savedContacts.length} {savedContacts.length === 1 ? 'person' : 'people'} added
                  {savedContacts.length < inviteGoal && (
                    <span className="text-muted-foreground font-normal">
                      {' '}— add {inviteGoal - savedContacts.length} more to reach your goal
                    </span>
                  )}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {savedContacts.map((c, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-background border border-border text-sm"
                    >
                      {c.name}
                      <button
                        type="button"
                        onClick={() => removeSavedContact(i)}
                        className="p-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        aria-label={`Remove ${c.name}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
                {savedContacts.length < 15 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openAddForm()}
                    className="mt-3 gap-1.5 border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add another person
                  </Button>
                )}
              </div>
            )}

            {/* Add form: always visible when 0 contacts; when 1+ contacts, show when showAddForm */}
            {savedContacts.length < 15 && (savedContacts.length === 0 || showAddForm) && (
              <div className="group relative grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 p-4 rounded-lg bg-secondary/50 border border-border transition-colors mb-4 items-end">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Full name"
                    value={draftContact.name}
                    onChange={(e) => updateDraft('name', e.target.value)}
                    className="pl-9 bg-card"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="e.g. them@gmail.com"
                    value={draftContact.email}
                    onChange={(e) => updateDraft('email', e.target.value)}
                    onBlur={() => {
                      const fixed = fixEmailDomainTypo(draftContact.email);
                      updateDraft('email', fixed);
                      setDraftEmailError(getEmailError(fixed));
                    }}
                    className={cn('pl-9 bg-card', draftEmailError && 'border-destructive')}
                    required
                  />
                  {draftEmailError && (
                    <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive whitespace-nowrap">{draftEmailError}</p>
                  )}
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="tel"
                    placeholder="e.g. 0712345678"
                    value={draftContact.phone}
                    onChange={(e) => updateDraft('phone', e.target.value)}
                    onBlur={() => {
                      const normalized = normalizeKenyanPhone(draftContact.phone);
                      updateDraft('phone', normalized);
                      setDraftPhoneError(getPhoneError(normalized));
                    }}
                    className={cn('pl-9 bg-card', draftPhoneError && 'border-destructive')}
                    required
                  />
                  {draftPhoneError && (
                    <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive whitespace-nowrap">{draftPhoneError}</p>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={addContact}
                  disabled={
                    !draftContact.name.trim() ||
                    !draftContact.email.trim() ||
                    !draftContact.phone.trim() ||
                    !!draftEmailError ||
                    !!draftPhoneError ||
                    !isValidEmail(draftContact.email) ||
                    !isValidKenyanPhone(draftContact.phone)
                  }
                  className="self-center gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90 disabled:opacity-50"
                >
                  <UserPlus className="w-4 h-4" />
                  Add member
                </Button>
              </div>
            )}

            {/* Progress bar: segments = inviteGoal (3, 5, or 10) */}
            <div className="mt-6 mb-2">
              <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                <span>{savedContacts.length} of {inviteGoal} (min 5)</span>
                <span>15 max</span>
              </div>
              <div className="flex gap-1 w-full">
                {Array.from({ length: inviteGoal }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                      i < savedContacts.length ? PROGRESS_COLORS[i % PROGRESS_COLORS.length] : 'bg-secondary'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-center gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(1)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-accent text-accent-foreground hover:bg-gold-light px-10 py-6 text-base font-semibold rounded-xl gap-2 shadow-lg shadow-accent/20 transition-all hover:shadow-xl hover:shadow-accent/30 hover:-translate-y-0.5"
            >
              <Send className="w-5 h-5" />
              {isSubmitting ? 'Sending Invitations...' : 'Send Invitations'}
            </Button>
          </div>
        </>
      )}

      {/* Thank you modal gate — shown after successful submit */}
      {showThankYouModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="thank-you-title"
        >
          <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-6 md:p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-accent" />
            </div>
            <h2 id="thank-you-title" className="text-xl font-semibold text-foreground mb-2">
              Thank you!
            </h2>
            <p className="text-foreground mb-3">
              We've sent <strong>{thankYouInviteCount} invitation{thankYouInviteCount !== 1 ? 's' : ''}</strong> on your behalf.
            </p>
            <div className="text-left bg-secondary/50 rounded-lg p-4 mb-6 text-sm text-muted-foreground space-y-2">
              <p className="font-medium text-foreground">What happens next:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Each invitee will receive an <strong>email</strong> with a link to accept the invitation.</li>
                <li>They can click &quot;Accept invitation&quot; in that email to join the Homeland Ecosystem.</li>
                <li>You'll be kept in the loop as they join.</li>
              </ul>
            </div>
            <Button
              type="button"
              onClick={() => setShowThankYouModal(false)}
              className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
